import { z } from 'zod';

import { FormatType } from './format';
import { ResourceType } from './resource';

export const CustomDatetimeSchema = z.union([
  // ISO 8601 format datetime string: e.g. "2023-06-06T13:28:29"
  z.preprocess(
    input => {
      if (typeof input !== 'string') return input;
      const tFormatted = input.replace(' ', 'T');
      if (tFormatted.includes('.')) {
        const [datePart, nanoPart] = tFormatted.split('.');
        return `${datePart}.${nanoPart.substring(0, 3)}`;
      }
      return tFormatted;
    },
    z.string().datetime({ local: true }),
  ),
  // ISO 8601 format datetime string w/o seconds: e.g. "2023-09-21T14:19"
  z.preprocess(
    input => {
      if (typeof input !== 'string') return input;
      const tFormatted = input.replace(' ', 'T');
      return tFormatted.length === 16 ? `${tFormatted}:00` : tFormatted;
    },
    z.string().datetime({ local: true }),
  ),
  // Locale format datetime string: e.g. "Apr 29, 2026, 12:59:35 PM"
  z.preprocess(
    input => {
      if (typeof input !== 'string') return input;
      const date = new Date(input);
      if (!isNaN(date.getTime())) {
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
      }
      return input;
    },
    z.string().datetime({ local: true }),
  ),
]);

export type Datatype = Capitalize<ResourceType>;

export type Datatype_format = Capitalize<FormatType>;

export const GlytoucanApiResponseSchema = z.object({
  errcode: z.string(),
  logs: z.object({
    url: z.string(),
  }),
});

export const AccessionsCountSchema = z.object({
  contents: z.object({
    count: z.preprocess(
      input => parseInt(input as string, 10),
      z.number().int().nonnegative(),
    ),
  }),
});

export const TokenExpireTimeSchema = z.object({
  contents: z.preprocess(
    input => (Array.isArray(input) ? input[0] : input),
    z.object({
      expiration: z.coerce.number().int().nonnegative(),
    }),
  ),
});

export const UserProfileSchema = z.object({
  contents: z.object({
    contributor_id: z.string(),
    primary_mail: z.union([
      z.string().email(),
      z.string().length(0),
      z.null(),
      z.undefined(),
    ]),
    registration_date: z.union([
      CustomDatetimeSchema,
      z.string().length(0),
      z.null(),
      z.undefined(),
    ]),
    token: z.string().optional(),
  }),
});

export const CountGlycoCTSchema = z.object({
  contents: z.object({
    count: z.preprocess(
      input => parseInt(input as string, 10),
      z.number().int().nonnegative(),
    ),
  }),
});

export const CountWURCSSchema = z.object({
  contents: z.object({
    count: z.preprocess(
      input => parseInt(input as string, 10),
      z.number().int().nonnegative(),
    ),
  }),
});

// 型をエクスポート
export type UserProfileType = z.infer<typeof UserProfileSchema>;

export const Upid2SeqSchema = z.string().regex(/^[A-Z]+$/);

const EmptyStringSchema = z.string().length(0);

// glycan entry information
export const CommonResourceSchema = z.object({
  accession: z.string(),
  accession_date: CustomDatetimeSchema,
  wurcstext: z.string(),
  archetype_flag: z.boolean().nullish(),

  id: z.number().int(),
  initial_registration_date: CustomDatetimeSchema,
  initial_registration_flag: z.boolean(),
  initial_registration_log: z.string(),
  last_registration_date: CustomDatetimeSchema,
  last_registration_flag: z.boolean(),
  last_registration_log: z.string(),
  archetype: z
    .object({
      child_glycan: z.array(
        z.object({
          acc: z.string().length(8),
          redend_anomeric_type: z.string(),
          redend_ringstype: z.string(),
        }),
      ),
    })
    .optional(),

  wurcs: z.string(),
  glycoct: z.string(),
  archetype_test: z.boolean().optional(),
  datatext: z.string().includes('\t'),
  datatype: z.string(),
  errcode: z.string().optional(),
});

export const CommonAccessionSchema = z.object({
  accession: z.string().length(8),
  accession_date: CustomDatetimeSchema.optional(),
  archetype_flag: z.boolean().nullish(),
  wurcstext: z.string().optional(),
});

export const CommonSubmissionSchema = CommonResourceSchema.extend({
  user_id: z.number().int(),
  datatext_id: z.number().int().optional(),

  reception: z.string().includes('\t'),
  reception_number: z.string().uuid(),
  reception_date: CustomDatetimeSchema,
  reception_log: z.string(),

  uniquenize_date: z.union([CustomDatetimeSchema, EmptyStringSchema]),
  uniquenize_log: z.string(),

  // overrides
  datatext: z.union([z.string().includes('\t'), EmptyStringSchema]),
  accession: z.union([
    z
      .string()
      .length(8)
      .regex(/^G[A-Z0-9]{7}$/),
    EmptyStringSchema,
  ]),
  accession_date: z.union([CustomDatetimeSchema, EmptyStringSchema]),
  objectification_date: z.union([CustomDatetimeSchema, EmptyStringSchema]),
  objectification_number: z.union([z.string().uuid(), EmptyStringSchema]),
  publication_date: z.union([CustomDatetimeSchema, EmptyStringSchema]),
});
